// /chord-craft/loop — the Loop Room, Chord Craft's jam space. Pick a
// progression, key, color level (plain/warm/lush), voicing level, and
// tempo, then loop it: a lookahead Web Audio transport plays metronome
// clicks plus voiced chords, chaining each step's voicing through the
// previous one's (`voiceChord(spec, level, { prev })`) so the loop moves
// smoothly instead of jumping octaves every bar. Progression cards light up
// on the step that's currently sounding, with a row of beat dots for the
// beat within that step. Play starts with one bar of count-in clicks before
// the first chord. Below the transport sits a wide, input-enabled
// CraftPiano (~A2–C6, screen + MIDI) for free jamming over the loop —
// nothing there is judged, though optional "note hints" dots mark scale and
// chord tones. A backing-volume slider scales the loop's gain, a "bass
// only" toggle drops the right hand out of the backing so you can comp it
// yourself, a metronome toggle silences the per-beat click (count-in still
// clicks), and a "feel" picker swaps the right hand's comp rhythm (held /
// pulse / push) while the left hand keeps its one anchoring hit per chord.
//
// Scheduling follows the piano-drills lookahead pattern
// (components/pianoDrills/useDrillTransport.js): a ~100ms setInterval looks
// ~0.25s into the audio clock's future and schedules everything that falls
// inside that window, with a matching setTimeout driving the "active step"
// React state so the UI lands in sync with what's actually sounding.
// Metronome clicks go through pianoDrillsAudio's absolute-time playClickAt;
// chords go through homeKeyAudio's playNote, whose `whenMs` is relative to
// "now" rather than absolute — so every scheduled time is converted against
// the same audioNow() sample taken once per tick, the same trick
// useDrillTransport uses for its setTimeout delays.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PROGRESSIONS,
  VOICING_LEVELS,
  chordPcs,
  progressionChords,
  voiceChord,
} from "../lib/chordCraft";
import { KEYS, MAJOR_SCALE_SEMIS, keyByName } from "../lib/pianoDrills";
import { audioNow, ensureAudio, playClickAt } from "../lib/pianoDrillsAudio";
import { PATCHES, playNote, resumeAudio, startNote } from "../lib/homeKeyAudio";
import { useMidiInput } from "../components/synthlab/useSynthInput";
import CraftShell from "../components/chordCraft/CraftShell";
import CraftPiano from "../components/chordCraft/CraftPiano";
import { cn } from "../lib/utils";

const STORAGE_KEY = "chordCraft:loop:v1";

const PIANO_LOW = 40; // E2 — covers voiceChord's full left-hand range (lhLow 40)
const PIANO_HIGH = 84; // C6

const TICK_MS = 100; // setInterval poll rate
const LOOKAHEAD_SEC = 0.25; // how far into the audio clock's future we schedule
const START_DELAY_SEC = 0.15; // pad before the first beat so playback never races the click

const MIN_TEMPO = 60;
const MAX_TEMPO = 140;

const COUNT_IN_BEATS = 4; // one bar, always 4/4 regardless of the progression's own meter

// Natural minor scale offsets from the tonic — mirrors MAJOR_SCALE_SEMIS'
// shape (pianoDrills.js only has the major one) for minor-mode progressions'
// note hints.
const NATURAL_MINOR_SEMIS = [0, 2, 3, 5, 7, 8, 10];

const normPc = (n) => ((n % 12) + 12) % 12;

// Color knob for progressionChords — plain triads, warm (default sevenths),
// or lush (sevenths + each step's own extensions). Not part of Agent A's
// export list (it's just a string enum on progressionChords), so the picker
// copy lives here.
const COLOR_LEVELS = [
  { id: "plain", label: "Plain", blurb: "triads only" },
  { id: "warm", label: "Warm", blurb: "sevenths on the chords that want them" },
  { id: "lush", label: "Lush", blurb: "sevenths plus each step's own extensions" },
];

// Right-hand comp "feel" — per-4-beat-bar event lists of { at, durBeats }
// (both in beats, `at` may be fractional). "held" isn't listed here: it's
// the original one-hit-sustained-for-the-whole-chord behavior, handled
// separately in tick() since its duration spans the whole chord rather than
// a single bar. Chords longer than 4 beats repeat whichever pattern's
// active once per bar; a 4-beat (or shorter) chord just plays it once.
const FEEL_LEVELS = [
  { id: "held", label: "Held", blurb: "one hit, held for the chord's full length" },
  { id: "pulse", label: "Pulse", blurb: "half notes on beats 1 and 3" },
  { id: "push", label: "Push", blurb: "1, and-of-2, 4 — the classic pop comp" },
];
const FEEL_PATTERNS = {
  pulse: [
    { at: 0, durBeats: 2 },
    { at: 2, durBeats: 2 },
  ],
  push: [
    { at: 0, durBeats: 1.5 },
    { at: 1.5, durBeats: 1.5 },
    { at: 3, durBeats: 1 },
  ],
};

const loadSaved = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

// Index of the last beat-offset at or before `beat` — same "latest onset
// wins" scan as pianoDrills.eventIndexAtStep, generalized to the Loop Room's
// per-progression beat grid instead of a fixed 16th-note one.
const stepIndexAtBeat = (offsets, beat) => {
  let idx = -1;
  for (let i = 0; i < offsets.length; i += 1) {
    if (offsets[i] <= beat) idx = i;
    else break;
  }
  return idx;
};

const Chip = ({ active, onClick, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-pressed={active}
    className={cn(
      "rounded-full px-3 py-1.5 text-sm transition-colors",
      active
        ? "bg-sky-400 font-medium text-stone-950"
        : "bg-stone-800 text-stone-300 hover:bg-stone-700"
    )}
  >
    {children}
  </button>
);

export default function ChordCraftLoop() {
  const saved = useMemo(loadSaved, []);

  const [progId, setProgId] = useState(
    () =>
      saved.progId ||
      (PROGRESSIONS.find((p) => p.id === "axis") || PROGRESSIONS[0] || {}).id
  );
  const [keyName, setKeyName] = useState(() => saved.keyName || "C");
  const [colorLevel, setColorLevel] = useState(() => saved.colorLevel || "warm");
  const [voicingLevel, setVoicingLevel] = useState(() => saved.voicingLevel || 2);
  const [tempo, setTempo] = useState(() => saved.tempo || 96);
  const [backingVolume, setBackingVolume] = useState(() =>
    saved.backingVolume == null ? 0.75 : saved.backingVolume
  );
  const [bassOnly, setBassOnly] = useState(() => Boolean(saved.bassOnly));
  const [metronomeOn, setMetronomeOn] = useState(() =>
    saved.metronomeOn == null ? true : Boolean(saved.metronomeOn)
  );
  const [feel, setFeel] = useState(() => saved.feel || "held");
  const [noteHints, setNoteHints] = useState(() =>
    saved.noteHints == null ? true : Boolean(saved.noteHints)
  );

  const [playing, setPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [currentBeat, setCurrentBeat] = useState(-1);
  const [countInBeat, setCountInBeat] = useState(null); // 0-3 while count-in clicks are playing, else null
  const [activeMidis, setActiveMidis] = useState(() => new Set());

  useEffect(() => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          progId,
          keyName,
          colorLevel,
          voicingLevel,
          tempo,
          backingVolume,
          bassOnly,
          metronomeOn,
          feel,
          noteHints,
        })
      );
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [
    progId,
    keyName,
    colorLevel,
    voicingLevel,
    tempo,
    backingVolume,
    bassOnly,
    metronomeOn,
    feel,
    noteHints,
  ]);

  const prog = useMemo(
    () => PROGRESSIONS.find((p) => p.id === progId) || PROGRESSIONS[0] || null,
    [progId]
  );

  const chords = useMemo(
    () => (prog ? progressionChords(prog, keyName, colorLevel) : []),
    [prog, keyName, colorLevel]
  );

  // Chain voiceChord through `prev` so every step moves as little as
  // possible from the one before it. A single forward pass leaves the
  // wrap-around seam (last step -> first step) unvoiced, since the first
  // step's own voicing was computed with prev = null — so it re-chains a
  // second pass starting from the first pass's last voicing. voiceChord's
  // placement converges after this (the standard fixed-point trick for
  // looped voice leading), so the second pass's shapes are used throughout,
  // making every seam — including the loop-back one — move smoothly.
  // Computed once per progression/key/color/voicing-level change, not per
  // loop pass, so the shape stays stable while it's actually playing.
  const voicings = useMemo(() => {
    let prev = null;
    const firstPass = chords.map((chord) => {
      const v = voiceChord(chord.spec, voicingLevel, { prev });
      prev = v;
      return v;
    });
    prev = firstPass.length ? firstPass[firstPass.length - 1] : null;
    return chords.map((chord) => {
      const v = voiceChord(chord.spec, voicingLevel, { prev });
      prev = v;
      return v;
    });
  }, [chords, voicingLevel]);

  const stepBeats = useMemo(
    () => chords.map((c) => Number(c.beats) || (prog && prog.beatsPerChord) || 4),
    [chords, prog]
  );
  const beatOffsets = useMemo(() => {
    let acc = 0;
    return stepBeats.map((b) => {
      const offset = acc;
      acc += b;
      return offset;
    });
  }, [stepBeats]);
  const totalBeats = useMemo(() => stepBeats.reduce((a, b) => a + b, 0), [stepBeats]);

  /* ---------- transport: lookahead scheduler ---------- */

  const runRef = useRef({
    timer: null,
    timeouts: [],
    nextBeat: 0,
    nextTime: 0,
    countInLeft: 0,
  });
  const playingRef = useRef(false);

  // Live refs so tick() stays a stable callback (empty dep array) while
  // always reading current progression/tempo/volume/toggle state — same
  // idiom as useDrillTransport's drillRef/bpmRef/modeRef.
  const voicingsRef = useRef(voicings);
  voicingsRef.current = voicings;
  const stepBeatsRef = useRef(stepBeats);
  stepBeatsRef.current = stepBeats;
  const beatOffsetsRef = useRef(beatOffsets);
  beatOffsetsRef.current = beatOffsets;
  const totalBeatsRef = useRef(totalBeats);
  totalBeatsRef.current = totalBeats;
  const tempoRef = useRef(tempo);
  tempoRef.current = tempo;
  const bassOnlyRef = useRef(bassOnly);
  bassOnlyRef.current = bassOnly;
  const backingVolumeRef = useRef(backingVolume);
  backingVolumeRef.current = backingVolume;
  const metronomeOnRef = useRef(metronomeOn);
  metronomeOnRef.current = metronomeOn;
  const feelRef = useRef(feel);
  feelRef.current = feel;

  const tick = useCallback(() => {
    const total = totalBeatsRef.current;
    const offsets = beatOffsetsRef.current;
    const voicingsNow = voicingsRef.current;
    if (!total || !offsets.length || !voicingsNow.length) return;
    const run = runRef.current;
    const beatSec = 60 / tempoRef.current;
    const now = audioNow();

    const pushTimeout = (id) => {
      run.timeouts.push(id);
      if (run.timeouts.length > 256) run.timeouts.splice(0, 128);
    };

    while (run.nextTime < now + LOOKAHEAD_SEC) {
      const time = run.nextTime;

      // Count-in: one bar of clicks (first beat accented) before the loop's
      // first chord. Always clicks, even with the metronome toggle off, and
      // never touches currentStep/currentBeat so no progression card lights
      // — only the optional "1 2 3 4" count-in readout follows it.
      if (run.countInLeft > 0) {
        const beatInBar = COUNT_IN_BEATS - run.countInLeft;
        playClickAt(time, beatInBar === 0);
        const delay = Math.max(0, (time - now) * 1000);
        pushTimeout(setTimeout(() => setCountInBeat(beatInBar), delay));
        run.countInLeft -= 1;
        run.nextTime = time + beatSec;
        continue;
      }

      const beat = run.nextBeat;
      const absBeat = ((beat % total) + total) % total;
      const stepIdx = stepIndexAtBeat(offsets, absBeat);
      const isOnset = stepIdx >= 0 && offsets[stepIdx] === absBeat;

      // Metronome: every beat clicks (chord-change beat accented), unless
      // the metronome toggle is off — that toggle only silences the loop's
      // own clicks, never the count-in above.
      if (metronomeOnRef.current) playClickAt(time, isOnset);

      if (stepIdx >= 0) {
        const voicing = voicingsNow[stepIdx];
        const vel = backingVolumeRef.current;
        const beats = stepBeatsRef.current[stepIdx] || 4;

        if (isOnset && voicing && vel > 0.02) {
          // Left hand: one hit at the chord's onset, held for the chord's
          // full length — the bass anchors the bar no matter what feel or
          // right hand is doing.
          const durMs = beats * beatSec * 1000 * 0.92;
          const whenMs = Math.max(0, (time - now) * 1000);
          (voicing.left || []).forEach((midi) =>
            playNote(PATCHES.softKeys, midi, durMs, whenMs, vel)
          );
          // "held" feel: right hand mirrors the left hand, one hit
          // sustained for the whole chord (today's original behavior).
          if (!bassOnlyRef.current && feelRef.current === "held") {
            (voicing.right || []).forEach((midi) =>
              playNote(PATCHES.softKeys, midi, durMs, whenMs, vel)
            );
          }
        }

        // "pulse"/"push" feel: right hand plays a per-4-beat-bar pattern
        // instead of one sustained hit. `barBeat` is this beat's position
        // within the *current chord's own* bar (0-3; a chord longer than 4
        // beats repeats the pattern each bar). Any pattern event whose `at`
        // falls in [barBeat, barBeat+1) fires here, offset by its
        // fractional beat from this beat's onset time.
        if (!bassOnlyRef.current && feelRef.current !== "held" && voicing && vel > 0.02) {
          const chordStart = offsets[stepIdx];
          const barBeat = ((absBeat - chordStart) % 4 + 4) % 4;
          const events = FEEL_PATTERNS[feelRef.current] || [];
          events.forEach((ev) => {
            if (ev.at >= barBeat && ev.at < barBeat + 1) {
              const at = time + (ev.at - barBeat) * beatSec;
              const whenMs = Math.max(0, (at - now) * 1000);
              const durMs = ev.durBeats * beatSec * 1000 * 0.92;
              (voicing.right || []).forEach((midi) =>
                playNote(PATCHES.softKeys, midi, durMs, whenMs, vel)
              );
            }
          });
        }

        const beatWithinStep = absBeat - offsets[stepIdx];
        const delay = Math.max(0, (time - now) * 1000);
        pushTimeout(
          setTimeout(() => {
            setCountInBeat(null);
            if (isOnset) setCurrentStep(stepIdx);
            setCurrentBeat(beatWithinStep);
          }, delay)
        );
      }

      run.nextBeat = beat + 1;
      run.nextTime = time + beatSec;
    }
  }, []);

  const stop = useCallback(() => {
    const run = runRef.current;
    if (run.timer) clearInterval(run.timer);
    run.timer = null;
    run.timeouts.forEach(clearTimeout);
    run.timeouts = [];
    run.countInLeft = 0;
    playingRef.current = false;
    setPlaying(false);
    setCurrentStep(-1);
    setCurrentBeat(-1);
    setCountInBeat(null);
  }, []);

  const start = useCallback(() => {
    if (!voicingsRef.current.length || !totalBeatsRef.current) return;
    if (!ensureAudio()) return;
    resumeAudio();
    const run = runRef.current;
    const t0 = audioNow() + START_DELAY_SEC;
    run.nextBeat = 0;
    run.nextTime = t0;
    run.countInLeft = COUNT_IN_BEATS;
    playingRef.current = true;
    setPlaying(true);
    setCurrentStep(-1);
    setCurrentBeat(-1);
    setCountInBeat(null);
    run.timer = setInterval(tick, TICK_MS);
    tick();
  }, [tick]);

  const toggle = useCallback(() => {
    if (playingRef.current) stop();
    else start();
  }, [start, stop]);

  // A different progression, key, color, voicing level, tempo, or comp feel
  // invalidates whatever's already scheduled — stop rather than let it
  // desync (same posture as useDrillTransport stopping on drill/bpm
  // changes). Feel changes the right-hand event pattern the same way a
  // voicing-level change reshapes the notes, so it stops the transport too.
  // Backing volume, bass-only, and the metronome toggle are read live off
  // refs each tick, so they can change freely without interrupting playback.
  useEffect(() => {
    if (playingRef.current) stop();
  }, [progId, keyName, colorLevel, voicingLevel, tempo, feel, stop]);

  // Unmount cleanup: clears the interval and every pending setTimeout.
  useEffect(() => stop, [stop]);

  /* ---------- free-jam piano input (screen + MIDI, unjudged) ---------- */

  const heldRef = useRef({});

  const press = useCallback((midi) => {
    if (heldRef.current[midi]) return;
    resumeAudio();
    heldRef.current[midi] = startNote(PATCHES.softKeys, midi, 0.9);
    setActiveMidis((s) => {
      const n = new Set(s);
      n.add(midi);
      return n;
    });
  }, []);

  const release = useCallback((midi) => {
    const voice = heldRef.current[midi];
    if (voice) {
      voice.stop();
      delete heldRef.current[midi];
    }
    setActiveMidis((s) => {
      if (!s.has(midi)) return s;
      const n = new Set(s);
      n.delete(midi);
      return n;
    });
  }, []);

  // Unmount cleanup: release every held on-screen/MIDI voice.
  useEffect(() => {
    const held = heldRef.current;
    return () => {
      Object.values(held).forEach((v) => v.stop());
      Object.keys(held).forEach((k) => delete held[k]);
    };
  }, []);

  const { status: midiStatus, inputs: midiInputs } = useMidiInput(press, release, true);

  // A light guide, not judging: light the currently-sounding step's full
  // voicing on the piano — left hand amber, right hand sky — so you can see
  // the whole chord you're comping over. With "bass only" on, the right hand
  // still lights up as the suggestion for what your own hand could play.
  const pianoRoles = useMemo(() => {
    const roles = {};
    const voicing = currentStep >= 0 ? voicings[currentStep] : null;
    if (voicing) {
      (voicing.left || []).forEach((midi) => { roles[midi] = "left"; });
      (voicing.right || []).forEach((midi) => { roles[midi] = "right"; });
    }
    return roles;
  }, [currentStep, voicings]);

  // Jam guidance dots: every scale tone in the key gets a subtle 'scale'
  // hint; while a chord is sounding, that chord's own pitch classes get a
  // brighter 'chord' hint instead (chord wins over scale, same "this is
  // what's happening right now" priority as pianoRoles above). Stopped
  // (currentStep === -1) still shows the scale — just no chord layer. Empty
  // whenever the "note hints" toggle is off, so passing `hints={}` is just
  // this memo's natural result rather than a separate branch at render time.
  const pianoHints = useMemo(() => {
    if (!noteHints || !prog) return {};
    const key = keyByName(keyName);
    if (!key) return {};
    const scaleSemis = prog.mode === "minor" ? NATURAL_MINOR_SEMIS : MAJOR_SCALE_SEMIS;
    const scalePcs = new Set(scaleSemis.map((s) => normPc(key.tonicPc + s)));
    const chordPcSet =
      currentStep >= 0 && chords[currentStep]
        ? new Set(chordPcs(chords[currentStep].spec))
        : null;
    const hints = {};
    for (let midi = PIANO_LOW; midi <= PIANO_HIGH; midi += 1) {
      const pc = normPc(midi);
      if (chordPcSet && chordPcSet.has(pc)) hints[midi] = "chord";
      else if (scalePcs.has(pc)) hints[midi] = "scale";
    }
    return hints;
  }, [noteHints, prog, keyName, currentStep, chords]);

  const useFlats = Boolean((keyByName(keyName) || {}).useFlats);
  const selectedVoicingLevel =
    VOICING_LEVELS.find((l) => l.id === voicingLevel) || VOICING_LEVELS[0];
  const selectedColorLevel = COLOR_LEVELS.find((c) => c.id === colorLevel) || COLOR_LEVELS[0];

  return (
    <CraftShell
      room="loop"
      title="Loop Room"
      subtitle="progressions from everywhere — loop them, jam on them."
    >
      <div className="space-y-6">
        <div className="space-y-4 rounded-2xl border border-stone-800 bg-stone-900/80 p-4">
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-stone-500">
              Progression
            </p>
            <div className="flex flex-wrap gap-2">
              {PROGRESSIONS.map((p) => (
                <Chip key={p.id} active={p.id === progId} onClick={() => setProgId(p.id)}>
                  {p.name}
                </Chip>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-x-8 gap-y-4">
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-stone-500">
                Key
              </p>
              <div className="flex flex-wrap gap-1.5">
                {KEYS.map((k) => (
                  <Chip key={k.name} active={k.name === keyName} onClick={() => setKeyName(k.name)}>
                    {k.name}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-stone-500">
                Color
              </p>
              <div className="flex flex-wrap gap-1.5">
                {COLOR_LEVELS.map((c) => (
                  <Chip
                    key={c.id}
                    active={c.id === colorLevel}
                    onClick={() => setColorLevel(c.id)}
                    title={c.blurb}
                  >
                    {c.label}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-stone-500">
                Voicing level
              </p>
              <div className="flex flex-wrap gap-1.5">
                {VOICING_LEVELS.map((lvl) => (
                  <Chip
                    key={lvl.id}
                    active={lvl.id === voicingLevel}
                    onClick={() => setVoicingLevel(lvl.id)}
                    title={lvl.blurb}
                  >
                    {lvl.id} · {lvl.name}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-stone-500">
                Feel
              </p>
              <div className="flex flex-wrap gap-1.5">
                {FEEL_LEVELS.map((f) => (
                  <Chip
                    key={f.id}
                    active={f.id === feel}
                    onClick={() => setFeel(f.id)}
                    title={f.blurb}
                  >
                    {f.label}
                  </Chip>
                ))}
              </div>
            </div>
          </div>

          <p className="text-xs text-stone-500">
            {selectedColorLevel.label}: {selectedColorLevel.blurb} · {selectedVoicingLevel.name}:{" "}
            {selectedVoicingLevel.blurb}
          </p>

          <label className="flex w-fit items-center gap-3 text-sm text-stone-300">
            Tempo
            <input
              type="range"
              min={MIN_TEMPO}
              max={MAX_TEMPO}
              value={tempo}
              onChange={(e) => setTempo(Number(e.target.value))}
              className="accent-sky-400"
            />
            <span className="w-16 tabular-nums text-stone-400">{tempo} bpm</span>
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-stone-800 bg-stone-900/80 p-4">
          <button
            type="button"
            onClick={toggle}
            disabled={!chords.length}
            className={cn(
              "rounded-full px-5 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40",
              playing ? "bg-stone-700 text-stone-100" : "bg-sky-400 text-stone-950"
            )}
          >
            {playing ? "Stop" : "Play"}
          </button>

          {countInBeat != null && (
            <span
              className="text-sm font-medium tabular-nums text-stone-300"
              aria-live="polite"
            >
              {Array.from({ length: COUNT_IN_BEATS }, (_, b) => (
                <span
                  key={b}
                  className={cn("mx-0.5", b === countInBeat ? "text-sky-300" : "text-stone-600")}
                >
                  {b + 1}
                </span>
              ))}
            </span>
          )}

          <label className="flex items-center gap-3 text-sm text-stone-300">
            Backing volume
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={backingVolume}
              onChange={(e) => setBackingVolume(Number(e.target.value))}
              className="accent-sky-400"
            />
            <span className="w-10 tabular-nums text-stone-400">
              {Math.round(backingVolume * 100)}%
            </span>
          </label>

          <Chip
            active={bassOnly}
            onClick={() => setBassOnly((b) => !b)}
            title="Schedule left-hand notes only — practice voicing the right hand yourself"
          >
            Bass only
          </Chip>

          <Chip
            active={metronomeOn}
            onClick={() => setMetronomeOn((m) => !m)}
            title="Click on every beat while the loop plays — the count-in bar always clicks"
          >
            Metronome
          </Chip>

          <span className="ml-auto text-xs text-stone-500">
            MIDI: {midiStatus}
            {midiInputs.length ? ` (${midiInputs.join(", ")})` : ""}
          </span>
        </div>

        <div className="flex flex-wrap gap-2">
          {chords.map((chord, i) => (
            <div
              key={`${chord.roman}-${i}`}
              className={cn(
                "min-w-[4.5rem] rounded-xl border px-3 py-2 text-center transition-colors",
                i === currentStep
                  ? "border-sky-400 bg-sky-400/15 shadow-[0_0_16px_rgba(56,189,248,0.35)]"
                  : "border-stone-800 bg-stone-900/80"
              )}
            >
              <p
                className={cn(
                  "font-serif text-lg",
                  i === currentStep ? "text-amber-50" : "text-stone-200"
                )}
              >
                {chord.symbol}
              </p>
              <p className="text-xs text-stone-500">{chord.roman}</p>
              {i === currentStep && (
                <div className="mt-1.5 flex items-center justify-center gap-1" aria-hidden="true">
                  {Array.from({ length: stepBeats[i] || 4 }, (_, b) => (
                    <span
                      key={b}
                      className={cn(
                        "h-1.5 w-1.5 rounded-full transition-colors",
                        b === currentBeat ? "bg-sky-300" : "bg-stone-700"
                      )}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
          {!chords.length && <p className="text-sm text-stone-500">No progression selected.</p>}
        </div>

        {prog && (
          <div className="space-y-1">
            <p className="text-sm text-stone-400">
              Famous in: <span className="text-stone-300">{(prog.famousIn || []).join(", ")}</span>
            </p>
            <p className="text-xs italic text-stone-500">
              genres are doors, not fences — every progression here plays in any style you bring
              to it.
            </p>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-medium uppercase tracking-wide text-stone-500">
              Jam over the loop — on-screen or MIDI, nothing here is judged
            </p>
            <Chip
              active={noteHints}
              onClick={() => setNoteHints((v) => !v)}
              title="Show which notes are in the key, and (while a chord sounds) in the chord"
            >
              Note hints
            </Chip>
          </div>
          <CraftPiano
            low={PIANO_LOW}
            high={PIANO_HIGH}
            roles={pianoRoles}
            activeMidis={activeMidis}
            onPress={press}
            onRelease={release}
            useFlats={useFlats}
            showLabels
            hints={pianoHints}
          />
          {noteHints && (
            <p className="text-xs text-stone-500">
              dots = notes in the key · bright dots = notes in the chord sounding now
            </p>
          )}
        </div>
      </div>
    </CraftShell>
  );
}
